// Phase NAATI-MAX B1 — NAATI Certification Levels
// Authoritative knowledge base used by /translate/naati/levels (+EN mirror).
// Source: NAATI public scheme (naati.com.au/certification).
// Used for E-E-A-T: shows we know the difference between CT/CAT and the
// interpreter ladder (CPI → CI → CSHI/CSLI → CCI).

export type NaatiSide = "translator" | "interpreter";

export interface NaatiLevel {
  code: string;
  nameTh: string;
  nameEn: string;
  side: NaatiSide;
  level: number; // ordinal 1-5
  scopeTh: string;
  scopeEn: string;
  exampleDocsTh: string[];
  exampleDocsEn: string[];
  testedTh: string;
  testedEn: string;
}

export const NAATI_LEVELS: NaatiLevel[] = [
  {
    code: "CT",
    nameTh: "นักแปลรับรอง (Certified Translator)",
    nameEn: "Certified Translator (CT)",
    side: "translator",
    level: 2,
    scopeTh:
      "นักแปลระดับมาตรฐานที่ผ่านการทดสอบของ NAATI — รับงานเอกสารราชการและกฎหมายทั่วไป ครอบคลุมประมาณ 90% ของงานที่ลูกค้าคนไทยใช้ยื่นออสเตรเลีย",
    scopeEn:
      "Standard NAATI-tested translator. Handles civil, education, financial, and most legal documents — about 90% of all Thai → English work submitted to Australian authorities.",
    exampleDocsTh: [
      "สูติบัตร / ทะเบียนสมรส / ทะเบียนหย่า",
      "Transcript / ปริญญาบัตร",
      "ใบรับรองความประพฤติ (Police Clearance)",
      "Statement บัญชี / สลิปเงินเดือน",
      "ใบเปลี่ยนชื่อ / ทะเบียนบ้าน",
    ],
    exampleDocsEn: [
      "Birth / Marriage / Divorce certificates",
      "Academic transcript / Degree",
      "Royal Thai Police clearance",
      "Bank statement / Payslip",
      "Name change / House registration",
    ],
    testedTh: "ทดสอบโดย NAATI: แปล 2 ข้อความ 250 คำ + จริยธรรมวิชาชีพ — ต่ออายุทุก 3 ปี (CPD/Work practice)",
    testedEn: "NAATI test: two 250-word translations + professional ethics. Recertify every 3 years via CPD + work practice.",
  },
  {
    code: "CAT",
    nameTh: "นักแปลขั้นสูง (Certified Advanced Translator)",
    nameEn: "Certified Advanced Translator (CAT)",
    side: "translator",
    level: 3,
    scopeTh:
      "นักแปลขั้นสูง ผ่านการทดสอบความซับซ้อนทางเทคนิค — สำหรับเอกสารที่ต้องการความแม่นยำสูง เช่น คู่มือทางการแพทย์ สัญญาระหว่างประเทศ คำพิพากษาศาล",
    scopeEn:
      "Advanced translator tested on technical complexity. Used for medical manuals, complex international contracts, court judgments, scientific literature.",
    exampleDocsTh: [
      "คำพิพากษาศาล / คำสั่งศาล",
      "สัญญาทางการค้าระหว่างประเทศ",
      "คู่มือทางการแพทย์ / ใบรับรองแพทย์เฉพาะทาง",
      "เอกสารทรัพย์สินทางปัญญา / สิทธิบัตร",
    ],
    exampleDocsEn: [
      "Court judgments / orders",
      "Cross-border commercial contracts",
      "Medical manuals / specialist reports",
      "Patents / IP filings",
    ],
    testedTh: "ทดสอบ NAATI ขั้นสูง: 3 ข้อความเฉพาะทาง — ผู้ที่ผ่านมีจำนวนจำกัดในคู่ภาษาไทย-อังกฤษ",
    testedEn: "NAATI advanced test: three specialised passages. Pass rate is low; only a small pool holds CAT for Thai ↔ English.",
  },
  {
    code: "RPT",
    nameTh: "นักแปลที่ได้รับการรับรอง (Recognised Practising Translator)",
    nameEn: "Recognised Practising Translator",
    side: "translator",
    level: 1,
    scopeTh:
      "สำหรับคู่ภาษาที่ NAATI ยังไม่จัดสอบ — รับรองจากประสบการณ์การทำงานจริง (สำหรับภาษาชนกลุ่มน้อย/ภูมิภาค)",
    scopeEn:
      "For language pairs where NAATI does not run a formal test. Recognised via documented professional practice — typically for minority/regional languages.",
    exampleDocsTh: ["เอกสารภาษาภูมิภาค", "ภาษาชนเผ่า / กลุ่มชาติพันธุ์"],
    exampleDocsEn: ["Regional languages", "Indigenous / tribal languages"],
    testedTh: "ไม่มีข้อสอบ — รับรองตามชั่วโมงประสบการณ์ + อ้างอิงผู้เชี่ยวชาญ",
    testedEn: "No exam — recognised via documented hours of practice + professional referees.",
  },
  {
    code: "CPI",
    nameTh: "ล่ามชั่วคราว (Certified Provisional Interpreter)",
    nameEn: "Certified Provisional Interpreter (CPI)",
    side: "interpreter",
    level: 1,
    scopeTh: "ล่ามระดับเริ่มต้น — งานสนทนาทั่วไป ติดต่อหน่วยงาน/บริการชุมชน",
    scopeEn: "Entry-level interpreter for general dialogue and community service interactions.",
    exampleDocsTh: ["พบเจ้าหน้าที่ Centrelink", "ตั้งบัญชีธนาคาร", "นัดหมายโรงเรียน"],
    exampleDocsEn: ["Centrelink appointments", "Bank account setup", "School meetings"],
    testedTh: "ทดสอบ Dialogue Interpreting + จริยธรรม",
    testedEn: "Dialogue interpreting + ethics test.",
  },
  {
    code: "CI",
    nameTh: "ล่ามรับรอง (Certified Interpreter)",
    nameEn: "Certified Interpreter (CI)",
    side: "interpreter",
    level: 2,
    scopeTh: "ล่ามระดับมาตรฐาน — งานราชการที่ซับซ้อนขึ้น ประชุมธุรกิจ บริการสุขภาพทั่วไป",
    scopeEn: "Standard interpreter for higher-complexity government, business, and general health work.",
    exampleDocsTh: ["พบทนาย / Mediation", "ตรวจร่างกายทั่วไป", "ประชุมเจรจาธุรกิจ"],
    exampleDocsEn: ["Lawyer / mediation sessions", "General medical consultations", "Business negotiations"],
    testedTh: "ทดสอบ Dialogue + Consecutive + Sight Translation",
    testedEn: "Dialogue + consecutive + sight translation tests.",
  },
  {
    code: "CSHI",
    nameTh: "ล่ามเฉพาะทางสุขภาพ (Certified Specialist Health Interpreter)",
    nameEn: "Certified Specialist Health Interpreter (CSHI)",
    side: "interpreter",
    level: 3,
    scopeTh: "ล่ามเฉพาะทางการแพทย์ — แพทย์เฉพาะทาง การผ่าตัด สุขภาพจิต ความยินยอมทางการแพทย์",
    scopeEn: "Specialist medical interpreter — specialist clinics, surgery briefings, mental health, informed consent.",
    exampleDocsTh: ["โรงพยาบาลรัฐออสเตรเลีย", "Mental Health Tribunal", "ความยินยอมก่อนผ่าตัด"],
    exampleDocsEn: ["Australian public hospitals", "Mental Health Tribunal", "Pre-surgery informed consent"],
    testedTh: "ทดสอบเฉพาะทางสุขภาพ + วิชาชีพการแพทย์",
    testedEn: "Specialist health exam + medical-domain ethics.",
  },
  {
    code: "CSLI",
    nameTh: "ล่ามเฉพาะทางกฎหมาย (Certified Specialist Legal Interpreter)",
    nameEn: "Certified Specialist Legal Interpreter (CSLI)",
    side: "interpreter",
    level: 4,
    scopeTh: "ล่ามเฉพาะทางกฎหมาย — ศาล ตำรวจ ทนายความ ตรวจคนเข้าเมือง (DHA)",
    scopeEn: "Specialist legal interpreter — courts, police, immigration (DHA), legal counsel.",
    exampleDocsTh: ["ศาล Magistrates / District / Federal", "การสอบสวนตำรวจ", "AAT / Tribunal"],
    exampleDocsEn: ["Magistrates / District / Federal Court", "Police interviews", "AAT / migration tribunal"],
    testedTh: "ทดสอบเฉพาะทางกฎหมาย + ขั้นตอนศาลออสเตรเลีย",
    testedEn: "Specialist legal exam + Australian court procedure.",
  },
  {
    code: "CCI",
    nameTh: "ล่ามการประชุม (Certified Conference Interpreter)",
    nameEn: "Certified Conference Interpreter (CCI)",
    side: "interpreter",
    level: 5,
    scopeTh: "ระดับสูงสุด — ล่ามการประชุมระดับชาติและนานาชาติ (Simultaneous interpreting ในตู้)",
    scopeEn: "Highest level — national and international conference interpreters (booth-based simultaneous).",
    exampleDocsTh: ["APEC / ASEAN", "การประชุม UN", "รัฐสภาออสเตรเลีย"],
    exampleDocsEn: ["APEC / ASEAN summits", "UN meetings", "Australian Parliament"],
    testedTh: "ทดสอบ Simultaneous (Booth) + Consecutive + Conference Ethics",
    testedEn: "Simultaneous (booth) + consecutive + conference ethics.",
  },
];

export const NAATI_LEVELS_TRANSLATOR = NAATI_LEVELS.filter((l) => l.side === "translator");
export const NAATI_LEVELS_INTERPRETER = NAATI_LEVELS.filter((l) => l.side === "interpreter");
